/**
 * Knowledge-aware route plotting (M3 plan R9, spec §6.1). The graph holds only known (rumoured or charted) systems
 * and relays that are online in the state; "Avoid hostile" also drops systems whose danger is at or above
 * `avoidDanger`, never the start or the end. Search itself is the landed `planRoute` (fewest hops, then map length),
 * so a fully charted galaxy routes exactly as `planRoute(onlineOnly)` does. A UI-rate call: it may allocate.
 */
import { planRoute } from '../../galaxy/route'
import type { Galaxy } from '../../galaxy/types'
import type { ReachState } from '../../reach/types'
import type { ReachMapsConfig } from './config'
import { knowledgeOf, relayStatusOf, type DangerFn } from './knowledge'

export interface KnownRouteOptions {
  avoidHostile: boolean
  /** Danger per system index (R4's `dangerOf`); read only when `avoidHostile` is set. */
  danger: DangerFn
}

export type KnownRouteResult = { route: string[] } | { route: null; reason: 'unknown' | 'noRoute' }

export function planKnownRoute(galaxy: Galaxy, state: ReachState, fromId: string, toId: string, opts: KnownRouteOptions, cfg: ReachMapsConfig): KnownRouteResult {
  const ids = new Set(galaxy.systems.map((s) => s.id))
  if (!ids.has(fromId) || !ids.has(toId)) return { route: null, reason: 'unknown' }
  if (knowledgeOf(state, fromId) === 'unknown' || knowledgeOf(state, toId) === 'unknown') return { route: null, reason: 'unknown' }

  const avoid = new Set<string>()
  galaxy.systems.forEach((s, i) => {
    if (knowledgeOf(state, s.id) === 'unknown') avoid.add(s.id)
    else if (opts.avoidHostile && opts.danger(i) >= cfg.galaxy.avoidDanger) avoid.add(s.id)
  })
  // The state decides which relays are online (relinked ones come up, a reboot is not yet online).
  const relays = galaxy.relays.map((r) => ({ ...r, online: relayStatusOf(state, r) === 'online' }))
  // planRoute never avoids its own ends, so the start and the end stay usable even when hostile.
  const route = planRoute({ ...galaxy, relays }, fromId, toId, { onlineOnly: true, avoid })
  return route === null ? { route: null, reason: 'noRoute' } : { route }
}
